import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import ProviderForm from '../../components/Form/ProviderForm';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { Provider, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import {
  useProviders,
  useCreateProvider,
  useUpdateProvider,
  useDeleteProvider,
} from '../../hooks/useProviders';

const Providers: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | undefined>();
  // Memoize options to prevent infinite loops
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  // Fetch providers with API
  const { data: providers, isLoading, error, refetch } = useProviders(queryOptions);

  // Mutations
  const createMutation = useCreateProvider(() => {
    setShowForm(false);
    setEditingProvider(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateProvider(() => {
    setShowForm(false);
    setEditingProvider(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteProvider(() => {
    setTimeout(() => refetch(), 100);
  });

  const columns: TableColumn[] = [
    { key: 'name', label: 'Provider Name', sortable: true },
    { key: 'contactPerson', label: 'Contact Person', sortable: true },
    { key: 'email', label: 'Email', sortable: false },
    { key: 'phone', label: 'Phone', sortable: false },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{value}</span>
    ) },
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() }
  ];

  const handleEdit = (provider: Provider) => {
    setEditingProvider(provider);
    setShowForm(true);
  };

  const handleDelete = async (provider: Provider) => {
    if (!window.confirm(`Are you sure you want to delete ${provider.name}?`)) return;
    if (!provider?.id) return;

    try {
      // Pass full provider object to get documentId
      await deleteMutation.execute(provider);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAdd = () => {
    setEditingProvider(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Provider>) => {
    try {
      if (editingProvider?.id) {
        // Update existing provider
        await updateMutation.execute({ id: editingProvider.id, data });
      } else {
        // Create new provider
        await createMutation.execute({
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          contactPerson: data.contactPerson,
          status: data.status || 'active',
        });
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingProvider(undefined);
  };

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading providers. Please try again.</p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const handleExport = () => {
    const exportData = providers.map(provider => ({
      'Provider Name': provider.name,
      'Contact Person': provider.contactPerson,
      'Email': provider.email,
      'Phone': provider.phone,
      'Address': provider.address,
      'Status': provider.status,
      'Created': new Date(provider.createdAt).toLocaleDateString()
    }));
    
    exportToExcel(exportData, `providers_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = providers.map(provider => ({
      'Provider Name': provider.name,
      'Contact Person': provider.contactPerson,
      'Email': provider.email,
      'Phone': provider.phone,
      'Address': provider.address,
      'Status': provider.status,
      'Created': new Date(provider.createdAt).toLocaleDateString()
    }));
    
    printAsExcel(exportData, 'Providers');
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <ProviderForm
          provider={editingProvider}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Providers</h1>
          <p className="text-gray-600">Manage your suppliers and service providers</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
          >
            Export
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Print
          </button>
        </div>
      </div>
      <Table
        columns={columns}
        data={providers}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title="Provider Directory"
        searchPlaceholder="Search providers..."
      />
    </div>
  );
};

export default Providers; 